import { useCallback } from 'react';

/**
 * Form wrapper that adds Enter key navigation:
 * - Enter on regular inputs: moves to next field
 * - Enter on textarea (without Shift): submits form
 * - Shift+Enter on textarea: newline
 * - Skips combobox/dropdown fields
 * 
 * Usage: Replace <form onSubmit={handleSubmit}> with <EnterForm onSubmit={handleSubmit}>
 */
export const EnterForm = ({ children, onSubmit, ...props }) => {
  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;

    const form = e.currentTarget;
    const tag = e.target.tagName.toLowerCase();

    // For textareas: Shift+Enter = newline, plain Enter = submit
    if (tag === 'textarea') {
      if (e.shiftKey) return;
      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) {
        e.preventDefault();
        submitBtn.click();
      }
      return;
    }

    // Don't intercept buttons
    if (tag === 'button') return;
    if (e.target.type === 'submit') return;

    // Don't intercept comboboxes or when dropdowns are open
    if (e.target.getAttribute('role') === 'combobox') return;
    if (e.target.closest('[role="listbox"], [role="combobox"]')) return;
    const openDropdown = form.querySelector('[aria-expanded="true"]');
    if (openDropdown) return;

    // Only handle standard input types
    const allowedTypes = ['text', 'number', 'date', 'email', 'tel', 'password'];
    if (tag === 'input' && !allowedTypes.includes(e.target.type)) return;

    // Get all focusable form elements
    const allInputs = Array.from(
      form.querySelectorAll('input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])')
    ).filter(el => el.offsetParent !== null && !el.closest('[aria-hidden="true"]'));

    const currentIndex = allInputs.indexOf(e.target);
    if (currentIndex === -1) return;

    // Find next visible element
    let nextIndex = currentIndex + 1;
    while (nextIndex < allInputs.length) {
      if (allInputs[nextIndex].offsetParent !== null) break;
      nextIndex++;
    }

    if (nextIndex >= allInputs.length) {
      // Last field — submit
      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn) {
        e.preventDefault();
        submitBtn.click();
      }
      return;
    }

    e.preventDefault();
    allInputs[nextIndex].focus();

    const nextType = allInputs[nextIndex].type;
    if (['text', 'number', 'email', 'tel'].includes(nextType)) {
      allInputs[nextIndex].select();
    }
  }, []);

  return (
    <form onSubmit={onSubmit} onKeyDown={handleKeyDown} {...props}>
      {children}
    </form>
  );
};
